import { useState, useEffect } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Upload, FileText, CheckCircle, AlertCircle, FileArchive } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BulkUpload = ({ user, onLogout }) => {
  const [matchingType, setMatchingType] = useState('case_number'); // 'case_number' or 'agreement_no'
  const [documentType, setDocumentType] = useState('Notice');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setMessage('');
    setError('');
    setResults(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('zipFile', file);
      formData.append('matchingType', matchingType);
      formData.append('documentType', documentType);

      const res = await axios.post(`${API_URL}/documents/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage(res.data.message);
      setResults(res.data.results);
      setFile(null);
      // Reset file input
      document.getElementById('zip-upload').value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1 className="page-title">Bulk Document Upload</h1>
        <p className="page-description">Upload a ZIP file containing PDFs named after Case Numbers or Agreement Numbers</p>
      </div>

      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Matching Criteria</label>
            <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="matchingType"
                  value="case_number"
                  checked={matchingType === 'case_number'}
                  onChange={(e) => setMatchingType(e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                />
                Case Number (e.g., TC_2_2026.pdf)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="matchingType"
                  value="agreement_no"
                  checked={matchingType === 'agreement_no'}
                  onChange={(e) => setMatchingType(e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                />
                Agreement No / LAN (e.g., LAN1234.pdf)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Document Type</label>
            <select
              className="form-select"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              <option value="notice">Notice</option>
              <option value="order">Order</option>
              <option value="award">Award</option>
              <option value="claim">Claim Document</option>
              <option value="evidence">Evidence</option>
              <option value="reply">Reply</option>
              <option value="rejoinder">Rejoinder</option>
              <option value="other">Miscellaneous / Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Select ZIP File</label>
            <div 
              style={{
                border: '2px dashed #e2e8f0',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                background: '#f7fafc',
                cursor: 'pointer'
              }}
              onClick={() => document.getElementById('zip-upload').click()}
            >
              <FileArchive size={48} color="#667eea" style={{ margin: '0 auto 1rem' }} />
              <input
                id="zip-upload"
                type="file"
                accept=".zip"
                onChange={(e) => setFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ fontWeight: 600, color: '#2d3748' }}>
                {file ? file.name : 'Click to select ZIP file'}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                Maximum file size: 50MB
              </div>
            </div>
          </div>

          {message && (
            <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle size={18} /> {message}
            </div>
          )}
          {error && (
            <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertCircle size={18} /> {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            disabled={loading || !file}
          >
            {loading ? 'Processing ZIP...' : <><Upload size={18} /> Upload and Process</>}
          </button>
        </form>
      </div>

      {results && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <h2 className="card-title">Processing Results</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ padding: '1rem', background: '#f0fff4', borderRadius: '8px', border: '1px solid #c6f6d5' }}>
              <div style={{ color: '#2f855a', fontWeight: 600 }}>Successfully Processed</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{results.successCount}</div>
            </div>
            <div style={{ padding: '1rem', background: '#fff5f5', borderRadius: '8px', border: '1px solid #fed7d7' }}>
              <div style={{ color: '#c53030', fontWeight: 600 }}>Failed / Not Found</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{results.failCount}</div>
            </div>
          </div>

          {results.details && results.details.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Identifier</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {results.details.map((detail, idx) => (
                  <tr key={idx}>
                    <td>{detail.filename}</td>
                    <td>{detail.identifier}</td>
                    <td>
                      <span className={`badge badge-${detail.status === 'success' ? 'ongoing' : 'new'}`}>
                        {detail.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>{detail.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </MainLayout>
  );
};

export default BulkUpload;
